/**
 * Extension for tracking and visualizing file drag-and-drop positions in the editor.
 *
 * When a file is dragged over the editor, this extension:
 * 1. Tracks the document position where the file would be dropped
 * 2. Displays a visual indicator (animated green line) showing the drop position
 * 3. Stores the position so the file upload handler can insert at the correct location
 *
 * The drop position is used by the file handler to insert uploaded files at the precise
 * location where the user dropped them, rather than at the current caret position.
 */

import {EditorState, StateEffect, StateField} from '@codemirror/state'
import {WidgetType} from '@codemirror/view'
import {Decoration, type DecorationSet, EditorView} from '@codemirror/view'

import styles from './drop-indicator.module.css'

/**
 * Simple widget that renders the drop position indicator.
 */
class DropIndicatorWidget extends WidgetType {
  override toDOM() {
    const indicator = document.createElement('div')
    indicator.className = styles.dropIndicator || ''
    return indicator
  }

  override eq() {
    // All drop indicator widgets are identical
    return true
  }

  override ignoreEvent() {
    return true
  }
}

/**
 * Represents the current drop position in the editor.
 * @property position - The character offset in the document where files would be dropped, or null if not dragging
 */
type DropPositionState = number | null

/** Effect to update drop position (null means not dragging) */
const setDropPositionEffect = StateEffect.define<DropPositionState>()

/**
 * State field to track the document position where a file would be dropped.
 * Position is null when not dragging, and contains the character offset during drag operations.
 */
const dropPositionField = StateField.define<DropPositionState>({
  create: () => null,
  update: (state, tr) => {
    for (const effect of tr.effects) {
      if (effect.is(setDropPositionEffect)) {
        return effect.value
      }
    }
    return state
  },
})

/**
 * Create decorations to show a visual indicator at the drop position.
 */
function dropPositionDecorations(position: DropPositionState): DecorationSet {
  if (position === null) {
    return Decoration.none
  }

  return Decoration.set([
    Decoration.widget({
      widget: new DropIndicatorWidget(),
      side: -1, // Position before the character
    }).range(position),
  ])
}

/**
 * Decoration provider that renders drop position indicators.
 * Only recomputes decorations when the dropPositionField changes (via the compare function),
 * not on every editor update.
 */
const dropPositionDecorationsField = StateField.define<DecorationSet>({
  create: state => dropPositionDecorations(state.field(dropPositionField)),
  update: (decorations, tr) => {
    // Check if the dropPositionField changed (by checking if the effect exists)
    const hasDropPositionEffect = tr.effects.some(e => e.is(setDropPositionEffect))
    if (hasDropPositionEffect) {
      // Recompute decorations only when the state actually changed
      const fieldState = tr.state.field(dropPositionField)
      return dropPositionDecorations(fieldState)
    }
    // If dropPositionField didn't change, preserve existing decorations
    return decorations
  },
  provide: field => EditorView.decorations.from(field),
})

function isFileDrag(event: DragEvent): event is DragEvent & {dataTransfer: DataTransfer} {
  return event.dataTransfer?.types.includes('Files') ?? false
}

/**
 * DOM event handlers for drag-and-drop file tracking.
 *
 * Processes four drag-and-drop events:
 * - dragenter: Enables drag-over state when files enter the editor
 * - dragover: Updates the drop position indicator as the user drags
 * - dragleave: Hides the indicator when dragging leaves the editor
 * - drop: Captures the final drop position for the file handler to use
 *
 * Only processes file drags (ignores non-file drags). Coordinates are converted
 * to document positions using CodeMirror's posAtCoords API.
 */
const dropPositionPlugin = EditorView.domEventHandlers({
  dragenter(event, view) {
    // Only handle file drags
    if (!isFileDrag(event)) return false

    view.dispatch({
      effects: [setDropPositionEffect.of(0)], // Set to 0 to indicate dragging (will be updated by dragover)
    })
    return true
  },

  dragover(event, view) {
    // Only handle file drags
    if (!isFileDrag(event)) return false

    // Get the document position from the drag coordinates
    const pos = view.posAtCoords({x: event.clientX, y: event.clientY})
    if (pos === null) return false

    // Update the drop position to show where it will be inserted
    view.dispatch({
      effects: [setDropPositionEffect.of(pos)],
    })

    event.dataTransfer.dropEffect = 'copy'
    event.preventDefault()
    return true
  },

  dragleave(event, view) {
    // Only clear if dragging files has actually left the entire editor
    // Check if the relatedTarget is outside the editor DOM tree
    if (!isFileDrag(event)) return false

    const relatedTarget = event.relatedTarget as Node | null
    if (relatedTarget && view.dom.contains(relatedTarget)) {
      // Still within the editor, don't clear the state
      return false
    }

    // We've left the editor entirely
    view.dispatch({
      effects: [setDropPositionEffect.of(null)],
    })
    return false
  },

  drop(event, view) {
    // Only handle file drops
    if (!isFileDrag(event)) return false

    // Get the document position from the drop coordinates
    const pos = view.posAtCoords({x: event.clientX, y: event.clientY})
    if (pos === null) return false

    // Update the drop position to the final position where files were dropped
    // Do NOT clear it yet - let the file handler read it and clear it when done
    view.dispatch({
      effects: [setDropPositionEffect.of(pos)],
    })

    return true
  },
})

/**
 * Retrieves the document position where files would be dropped.
 *
 * @param state - The CodeMirror editor state
 * @returns The character offset where files will be dropped, or null if no drop is in progress
 */
export function getDropPosition(state: EditorState): number | null {
  return state.field(dropPositionField, false) ?? null
}

/**
 * Clears the drop position after it has been used.
 * This ensures the position is not reused for subsequent operations.
 *
 * @param view - The CodeMirror editor view
 */
export function clearDropPosition(view: EditorView): void {
  view.dispatch({
    effects: [setDropPositionEffect.of(null)],
  })
}

export const dropPositionExtension = [dropPositionField, dropPositionDecorationsField, dropPositionPlugin]
